import type { IBeneficiaryRepository } from "@/application/ports/repositories/IBeneficiaryRepository";
import type {
  IFiscalQuotaRepository,
  QuotaListQuery,
} from "@/application/ports/repositories/IFiscalQuotaRepository";
import { isSubsidyEntry } from "@/domain/beneficiary";
import type { ExportDownloadOptions, ExportPreview } from "@/domain/export";
import type { QuotaReport } from "@/domain/fiscal/quotaReports";
import type { BulkResult } from "@/domain/types/bulk";
import type {
  QuotaAggregates,
  QuotaAggregatesParams,
  QuotaDetail,
  QuotaExportParams,
  QuotaListPage,
  QuotaSort,
} from "@/domain/fiscal/quotaList";
import { downloadBlob } from "../fileDownload";
import * as sdk from "../generated/sdk.gen";
import { unwrap, unwrapBlob, unwrapBlobWithFilename } from "../unwrap";

const toSortQuery = (sort: readonly QuotaSort[] | undefined) =>
  sort && sort.length > 0
    ? sort.map((entry) => `${entry.key}:${entry.direction}`).join(",")
    : undefined;

const listQuery = (params: QuotaListQuery | QuotaExportParams) => ({
  search: params.search,
  fiscal_year: params.fiscal_year,
  state: params.state,
  incentive_type: params.incentive_type,
  beneficiary_uuid: params.beneficiary_uuid,
});

export class FiscalQuotaRepository implements IFiscalQuotaRepository {
  constructor(
    private readonly beneficiaries: Pick<
      IBeneficiaryRepository,
      "createFiscalQuota" | "updateFiscalQuota"
    >,
  ) {}

  createFiscalQuota(...args: Parameters<IFiscalQuotaRepository["createFiscalQuota"]>) {
    return this.beneficiaries.createFiscalQuota(...args);
  }

  updateFiscalQuota(...args: Parameters<IFiscalQuotaRepository["updateFiscalQuota"]>) {
    return this.beneficiaries.updateFiscalQuota(...args);
  }

  async getQuotas(params: QuotaListQuery = {}, signal?: AbortSignal): Promise<QuotaListPage> {
    const { items, total_count, total_unfiltered_count } = await unwrap(
      sdk.listAllFiscalQuotas({
        query: {
          ...listQuery(params),
          sort: toSortQuery(params.sort),
          limit: params.limit,
          offset: params.offset,
        },
        signal,
      }),
    );
    return {
      quotas: items,
      total_count,
      total_unfiltered_count: total_unfiltered_count ?? total_count,
    };
  }

  async getQuotaAggregates(
    params: QuotaAggregatesParams,
    signal?: AbortSignal,
  ): Promise<QuotaAggregates> {
    const response = await unwrap(
      sdk.getFiscalQuotaAggregates({
        query: { ...listQuery(params), aggregates: params.aggregates.join(",") },
        signal,
      }),
    );
    return response.aggregates as QuotaAggregates;
  }

  async getQuota(quotaUuid: string, signal?: AbortSignal): Promise<QuotaDetail> {
    const quota = await unwrap(
      sdk.getFiscalQuota({ path: { fiscal_quota_uuid: quotaUuid }, signal }),
    );
    return {
      ...quota,
      subsidies: quota.subsidies ? quota.subsidies.filter(isSubsidyEntry) : null,
    };
  }

  async bulkDeleteFiscalQuotas(quotaUuids: readonly string[]): Promise<BulkResult> {
    return unwrap(sdk.bulkDeleteFiscalQuotas({ body: { fiscal_quota_uuids: [...quotaUuids] } }));
  }

  async previewExport(params: QuotaExportParams): Promise<ExportPreview> {
    return unwrap(sdk.previewFiscalQuotasExport({ query: listQuery(params) }));
  }

  async downloadExport(params: QuotaExportParams, options: ExportDownloadOptions): Promise<void> {
    const { blob, filename } = await unwrapBlobWithFilename(
      sdk.exportFiscalQuotas,
      {
        query: {
          ...listQuery(params),
          sort: toSortQuery(params.sort),
          format: options.format,
          columns: options.columns.length > 0 ? options.columns.join(",") : undefined,
        },
      },
      `fiscal_quotas.${options.format}`,
    );
    downloadBlob(blob, filename);
  }

  async previewExpenseAudit(
    quotaUuid: string,
    request: { template_uuid: string },
  ): Promise<string> {
    const blob = await unwrapBlob(sdk.previewExpenseAudit, {
      path: { fiscal_quota_uuid: quotaUuid },
      body: request,
    });
    return URL.createObjectURL(blob);
  }

  async downloadExpenseAudit(quotaUuid: string, request: { template_uuid: string }): Promise<void> {
    const { blob, filename } = await unwrapBlobWithFilename(
      sdk.generateExpenseAudit,
      { path: { fiscal_quota_uuid: quotaUuid }, body: request },
      `expense_audit_${quotaUuid}.pdf`,
    );
    downloadBlob(blob, filename);
  }

  async listReports(quotaUuid: string, signal?: AbortSignal): Promise<QuotaReport[]> {
    const response = await unwrap(
      sdk.listFiscalQuotaReports({ path: { fiscal_quota_uuid: quotaUuid }, signal }),
    );
    return response.items.map((item) => ({
      ...item,
      title: item.title ?? null,
      generated_by_id: item.generated_by_id ?? null,
      download_url: item.download_url ?? null,
    }));
  }

  async deleteReport(quotaUuid: string, documentUuid: string): Promise<void> {
    await unwrap(
      sdk.deleteFiscalQuotaReport({
        path: { fiscal_quota_uuid: quotaUuid, document_uuid: documentUuid },
      }),
    );
  }

  async reopenConsolidation(beneficiaryUuid: string, quotaUuid: string): Promise<void> {
    await unwrap(
      sdk.reopenFiscalQuotaConsolidation({
        path: { beneficiary_uuid: beneficiaryUuid, fiscal_quota_uuid: quotaUuid },
      }),
    );
  }
}
